
// src\themes\default\elements\Header\PrimaryDrawer.tsx
'use client';

import { ChevronRight, X } from 'lucide-react';

import Link from 'next/link';

import { Button } from '@/components/ui/button';
import { MenuItem } from '@/core/types/menuApi';
import { useDrawerStore } from '@/stores/drawerStore';

type SidebarMenuProps = {
  mainItems: MenuItem[];
  introItems: MenuItem[];
  introDescription: string;
  mainDescription: string;
};

export default function PrimaryDrawer({
  mainItems,
  introItems,
  introDescription,
  mainDescription,
}: SidebarMenuProps) {
  const { primaryOpen, setPrimaryOpen, setSecondaryOpen, setSelectedItemId } =
    useDrawerStore();

  function handleClose() {
    setPrimaryOpen(false);
    setSecondaryOpen(false);
  }

  function handleItemClick(id: number) {
    setSelectedItemId(id);
    setSecondaryOpen(true);
  }

  return (
    <div
      className={`transition-transform no-scrollbar duration-300 ease-in-out bg-primary-0 overflow-y-auto pointer-events-auto fixed top-0 h-full z-50 lg:z-70 ${primaryOpen
        ? 'translate-x-0 w-full lg:w-80 ltr:border-r rtl:border-l border-primary-200'
        : 'ltr:-translate-x-full rtl:translate-x-full w-0 lg:w-80'
        } ltr:left-0 rtl:right-0`}
      role="navigation"
      aria-label="Primary navigation drawer"
    >
      <div className="p-6 min-h-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-8 pb-4 border-b border-primary-100">
          <span className="text-[13px] tracking-wide text-primary-500">
            {mainDescription}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Close drawer"
            className="rounded-full hover:bg-primary-100 text-primary-900"
          >
            <X className="w-5 h-5 stroke-[1.5]" />
          </Button>
        </div>

        {/* Main items */}
        <div className="mb-12">
          {mainItems.length > 0 && (
            <ul className="flex flex-col">
              {mainItems.map(item => (
                <li
                  key={item.id}
                  className="group relative border-b border-primary-100 last:border-0"
                  onMouseEnter={() => {
                    if (
                      window.innerWidth >= 1024 &&
                      item.children?.length > 0
                    ) {
                      handleItemClick(item.id);
                    }
                  }}
                >
                  <div className="flex items-center justify-between cursor-pointer min-h-13 group-hover:bg-primary-50 rounded-sm px-2">
                    <Link
                      href={item.link || '#'}
                      className="flex-1 py-3"
                      onClick={() => {
                        handleClose();
                      }}
                    >
                      <span className="text-[13px] tracking-wide text-primary-800">{item.title}</span>
                    </Link>

                    {item.children && item.children.length > 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleItemClick(item.id)}
                        aria-label={`Open submenu for ${item.title}`}
                        className="rounded-full hover:bg-primary-100"
                        aria-expanded={
                          useDrawerStore.getState().secondaryOpen &&
                          useDrawerStore.getState().selectedItemId === item.id
                        }
                      >
                        <ChevronRight className="w-4 h-4 stroke-[1.5] rtl:rotate-180 text-primary-500" />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Intro items */}
        <div>
          {introItems.length > 0 && (
            <div>
              {introDescription && (
                <span className="mb-4 block text-[13px] tracking-wide text-primary-500">
                  {introDescription}
                </span>
              )}
              <ul className="flex flex-col gap-y-1">
                {introItems.map(item => (
                  <li key={item.id} onClick={handleClose}>
                    <Link
                      href={item.link || '#'}
                      className="text-primary-600 text-[13px] hover:bg-primary-50 focus:bg-primary-50 rounded-sm px-2 py-3 block"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
